import { TextField } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import { DropzoneArea } from "material-ui-dropzone";
import { useDataProvider, useNotify } from "ra-core";
import React from "react";
import { nowToAppLocalDateTime } from "../../../utils/date";
import { deliveryActions } from "../util";

interface Props {
  selectedIds?: number[];
  onComplete?: () => void;
}

const StartMultipleDeliveryDialog: React.FC<Props> = (props) => {
  const dataProvider = useDataProvider();
  const { selectedIds, onComplete } = props;
  const [open, setOpen] = React.useState(false);
  const [pickupNote, setPickupNote] = React.useState("");
  const [pickedImages, setPickedImages] = React.useState<File[]>([]);
  const [startableDeliveries, setStartableDeliveries] = React.useState<any[]>(
    [],
  );
  const [isLoading, setIsLoading] = React.useState(false);
  const notify = useNotify();
  const pickedTime = nowToAppLocalDateTime();

  const handleClose = () => {
    setOpen(false);
    setPickupNote("");
    setPickedImages([]);
  };

  const handleSelectFile = (files: File[]) => setPickedImages(files);

  const handleAccept = async () => {
    if (startableDeliveries.length === 0) {
      notify("No deliveries can be started", "warning");
      return;
    }

    setIsLoading(true);

    try {
      const updatePromises = startableDeliveries.map((delivery) => {
        const formData = new FormData();
        formData.append("action", deliveryActions.START);
        formData.append("picked_time", pickedTime);
        formData.append(
          "picked_location_id",
          String(delivery.product_instance?.location_id ?? ""),
        );
        formData.append("pickup_note", pickupNote);

        pickedImages.forEach((file, index) => {
          formData.append(`picked_images[${index}][url]`, file);
          formData.append(`picked_images[${index}][type]`, "image");
        });

        return dataProvider.update("deliveries", {
          id: delivery.id,
          data: formData,
          previousData: delivery,
          query: {
            _method: "PUT",
          },
        } as any);
      });

      await Promise.all(updatePromises);

      notify(
        `Successfully started ${startableDeliveries.length} deliveries.`,
        "info",
      );
      setOpen(false);
      setPickupNote("");
      setPickedImages([]);
      onComplete?.();
    } catch (error: any) {
      notify(`Error: ${error.message}`, "warning");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePickupNoteChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    setPickupNote(event.target.value);
  };

  const hasSelection = selectedIds && selectedIds.length > 0;

  const handleOpenDialog = async () => {
    if (!selectedIds || selectedIds.length === 0) {
      notify("Please select deliveries first", "warning");
      return;
    }

    try {
      // Fetch complete delivery data to get product_instance.location_id
      const deliveryPromises = selectedIds.map((id) =>
        dataProvider.getOne("deliveries", { id }),
      );
      const deliveryResponses = await Promise.all(deliveryPromises);
      const deliveries = deliveryResponses.map((response) => response.data);

      setStartableDeliveries(deliveries);
      setOpen(true);
    } catch (error: any) {
      notify(`Error loading delivery data: ${error.message}`, "warning");
    }
  };

  return (
    <>
      <Button
        color="primary"
        onClick={handleOpenDialog}
        size={"small"}
        disabled={!hasSelection}
      >
        Start Delivery {hasSelection ? `(${selectedIds.length})` : ""}
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="form-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="form-dialog-title">
          Start Multiple Deliveries
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {startableDeliveries.length > 0 ? (
              <>
                You have{" "}
                <strong>{startableDeliveries.length} deliveries</strong> that
                can be started.
                {selectedIds &&
                  selectedIds.length > startableDeliveries.length && (
                    <>
                      {" "}
                      ({selectedIds.length - startableDeliveries.length}{" "}
                      deliveries were filtered out as they are already
                      verified.)
                    </>
                  )}
                <br />
                All selected deliveries will be marked as picked up at the
                current time.
              </>
            ) : (
              <>
                No deliveries can be started. Verified deliveries cannot be
                started again.
              </>
            )}
          </DialogContentText>

          <TextField
            margin="dense"
            id="pickup-note"
            label="Pickup Note (Optional)"
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            value={pickupNote}
            onChange={handlePickupNoteChange}
            style={{ marginTop: 16, marginBottom: 16 }}
          />

          <DropzoneArea
            filesLimit={10}
            onChange={handleSelectFile}
            dropzoneText="Add pickup photos (Optional)"
          />

          {pickedImages.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <strong>Selected photos: {pickedImages.length}</strong>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleAccept}
            color="primary"
            disabled={startableDeliveries.length === 0 || isLoading}
          >
            {isLoading
              ? "Starting..."
              : `Start ${startableDeliveries.length} Deliveries`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StartMultipleDeliveryDialog;
